import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgClass, NgFor, NgIf, NgStyle} from '@angular/common';
import {CdkDragDrop, moveItemInArray, DragDropModule, CdkDragMove, CdkDrag} from '@angular/cdk/drag-drop';
import {
  CardQuestionnaireOptionDto,
  InteractionAnswerSaveDto,
  InteractionCardDto,
  ResultsDto,
} from '../../models/card.model';
import {CardResultsComponent} from '../card-results/card-results.component';
import {CardButtonComponent} from '../card-button/card-button.component';
import {ConferenceService} from '../../service/conference.service';
import {TranslateModule} from '@ngx-translate/core';

interface PairDto {
  isMatched: boolean;
  isPaired: boolean;
  distance: DragPosition;
  question: CardQuestionnaireOptionDto;
  answer: CardQuestionnaireOptionDto;
}

interface DragPosition {
  x: number;
  y: number;
}

@Component({
  selector: 'app-card-drag-and-match',
  templateUrl: './card-drag-and-match.component.html',
  styleUrls: ['./card-drag-and-match.component.scss'],
  standalone: true,
  imports: [NgStyle, NgClass, NgIf, NgFor, DragDropModule, CardResultsComponent, CardButtonComponent, TranslateModule],
})

export class CardDragAndMatchComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<InteractionAnswerSaveDto>();

  @Input() set card(card: InteractionCardDto) {
    this._card = card;
    this.isSubmitted = (card.cardAnswer && card.cardAnswer.isCompleted);

    this.initData();
    this.switchViews();
  }

  @Input() set isResults(isResults: boolean) {
    this._isResults = isResults;
    this.switchViews();
  }

  isEnableSwitchViews: boolean = false;
  isSubmitted: boolean = false;

  _isResults: boolean | null = null;
  isShowingResults: boolean | null = null;

  _card: InteractionCardDto = {} as InteractionCardDto;
  options: CardQuestionnaireOptionDto[] = [];
  answers: CardQuestionnaireOptionDto[] = [];
  results: ResultsDto | null = null;

  pairs: PairDto[] = [];
  dragPosition: { x: number, y: number }[] = [];
  isDragging: boolean = false;

  closestIndex: number = -1;
  private debounceMoveTimer: any;
  private debounceDropTimer: any;

  constructor(
    private conferenceService: ConferenceService,
  ) {
  }

  ngOnInit() {
  }

  initData() {
    this.options = this._card.interactionsCardItems.map(item => this.extractInteractionAnswer(item));
    this.options.sort((a, b) => {
      if (a.item.cardItemAnswer && b.item.cardItemAnswer) {
        return a.item.cardItemAnswer.value - b.item.cardItemAnswer.value;
      }
      return a.item.order - b.item.order;
    });

    if (this.isSubmitted) {
      this.pairs = this.options.map(o => {
        return {
          isMatched: false,
          isPaired: true,
          distance: {x: 0, y: 0},
          question: o,
          answer: this.options.find(a => a.item.id === o.item.cardItemAnswer.valueText)
        } as PairDto;
      });

      this.options = [];
    } else {
      this.answers = this.options.map(o => o);
      this.answers.sort((a, b) => {
        return a.item.correctOrder - b.item.correctOrder;
      });

      this.dragPosition = this.options.map((o, i) => {
        return {x: 0, y: 0};
      });

      this.pairs = this.options.map((o, i) => {
        return {
          isMatched: false,
          isPaired: false,
          distance: {x: 0, y: 0},
          question: o,
          answer: this.answers[i]
        };
      });
    }
  }

  onMoved(event: any, i: any) {
    if (this.debounceMoveTimer) {
      clearTimeout(this.debounceMoveTimer);
      this.debounceMoveTimer = null;
    }

    this.debounceMoveTimer = setTimeout(() => {
      this.pairs[i].distance = event.distance;
      this.closestIndex = this.getClosestIndex(event, i, this.pairs[i].distance);
      if (!this.isDragging) {
        this.onDrop(event, i, 'onMoved');
      }
      this.debounceMoveTimer = null;
    }, 100);
  }

  onDropStarted(event: any, i: any) {
    this.isDragging = true;

    const children = document.querySelectorAll('.item-left');
    children[i].classList.add('item-dragging');
  }

  onDropEnded(event: any, i: any) {
    this.isDragging = false;

    if (!this.debounceMoveTimer) {
      this.onDrop(event, i, 'onDropEnded');
    }
  }

  onDrop(event: any, i: any, source: any) {
    // console.log('onDrop', source, i, this.closestIndex, this.pairs[i].distance, this.pairs[i].isPaired);
    const children = document.querySelectorAll('.item-left');

    if (this.pairs[i].distance.x > 0 && this.closestIndex >= 0) {
      this.pairs[this.closestIndex].isPaired = true;
      if (this.closestIndex !== i) {
        this.pairs[i].isPaired = false;
        this.moveItem(i, this.closestIndex, 1);
        this.moveItem(this.closestIndex, i, -1);

        setTimeout(() => {
          this.swipePairs(i, this.closestIndex);
        }, 300);
      } else {
        this.moveItemSelf(i);
      }
    } else {
      this.pairs[i].isPaired = false;
      this.pairs[i].distance = {x: 0, y: 0};
      this.dragPosition[i] = this.pairs[i].distance;
    }

    setTimeout(() => {
      children[i].classList.remove('item-dragging');
    }, 300);
  }

  getClosestIndex(event: CdkDragMove, i: any, d: DragPosition) {
    const children: any = document.querySelectorAll('.item-left');
    const draggedTop = children[i]['offsetTop'] + d.y;
    const draggedHeight = children[i]['offsetHeight'];

    let closestIndex = -1;
    let smallestDistance = Number.MAX_VALUE;

    Array.from(children).forEach((child: any, index) => {
      const childTop = child['offsetTop'];

      // Calculate the vertical distance
      const distance = Math.abs(draggedTop - childTop);

      if (distance < smallestDistance && distance < draggedHeight / 2) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  moveItemSelf(currentIndex: any) {
    const children = document.querySelectorAll('.item-left');
    children[currentIndex].classList.add('cdk-drag-animating');
    this.dragPosition[currentIndex] = {x: 0, y: 0};
  }

  moveItem(previousIndex: any, currentIndex: any, direction = 1) {
    const children: any = document.querySelectorAll('.item-left');
    children[currentIndex].classList.add('cdk-drag-animating');
    const dy = children[currentIndex]['offsetTop'] - children[previousIndex]['offsetTop'];
    this.dragPosition[previousIndex] = {x: 10 * direction, y: dy};
  }

  swipePairs(previousIndex: any, currentIndex: any) {
    const children = document.querySelectorAll('.item-left');
    children[currentIndex].classList.remove('cdk-drag-animating');
    children[previousIndex].classList.remove('cdk-drag-animating');

    const question = this.pairs[previousIndex].question;
    this.pairs[previousIndex].question = this.pairs[currentIndex].question;
    this.pairs[currentIndex].question = question;
    this.pairs[previousIndex].isPaired = false;
    this.pairs[currentIndex].isPaired = true;

    this.dragPosition = this.dragPosition.map((d, i) => {
      this.pairs[i].distance = {x: 0, y: 0};
      return {x: 0, y: 0};
    });
  }

  isPaired() {
    return this.pairs.every(p => p.isPaired);
  }

  isPassed(pairs: PairDto[]) {
    return pairs.every(p => p.question.item.id === p.answer.item.id);
  }

  submit() {
    this.isSubmitted = true;
    const payload = {
      interactionCardId: this._card.id,
      score: this._card.score,
      isCompleted: true,
      isPassed: this._card.isNoCorrect || this.isPassed(this.pairs),
      groupExtId: 0,
      text: '',
      InteractionAnswerItems: this.pairs.map(p => ({
        interactionsCardItemId: p.question.item.id,
        value: 0,
        valueText: p.answer.item.id,
        score: p.question.item.score,
      })),
    } as InteractionAnswerSaveDto;

    this.onSubmit.emit(payload);
  }

  private extractInteractionAnswer(item: any) {
    return {
      isSelected: item.cardItemAnswer && item.cardItemAnswer.value > 0 ? true : false,
      value: item.cardItemAnswer && item.cardItemAnswer.value > 0 ? item.cardItemAnswer.value : 0,
      valueText: item.cardItemAnswer ? item.cardItemAnswer.valueText : null,
      item
    };
  }

  switchViews() {
    if (this._isResults === null) {
      return;
    }

    if (this._isResults) {
      this.showResults();
    } else {
      this.hideResults();
    }
  }

  showResults() {
    this.conferenceService.getStatistics(this._card.id, 0)
      .then((statistics) => {
        console.log('statistics', statistics);

        const percentPassed = statistics.interactionCardAnswersCount > 0 ? statistics.interactionCardAnswersPassed / statistics.interactionCardAnswersCount : 0;
        const percentFailed = 1 - percentPassed;

        this.results = {
          items: [
            {
              value: percentPassed,
              label: (percentPassed * 100).toFixed(0) + '%',
              title: 'Correct answers',
              status: 'positive',
            },
            {
              value: percentFailed,
              label: (percentFailed * 100).toFixed(0) + '%',
              title: 'Incorrect answers',
              status: 'negative',
            },
          ]
        };

        this.isShowingResults = true;

      });
  }

  hideResults() {
    this.isShowingResults = false;
  }

  protected readonly postMessage = postMessage;
}
